/**
 * Générateur — Additionner et soustraire des nombres décimaux
 * Attendu ID : 609 | Niveau : 6ème | Domaine : Nombres et calculs
 * Cluster 6 — Addition / Soustraction de décimaux
 *
 * Types générés (7 templates × formats) :
 *   calcul_direct_qcm_unique, calcul_direct_texte, calcul_direct_relier, calcul_direct_tableau_vf
 *   chiffre_manquant_qcm_unique, chiffre_manquant_texte, chiffre_manquant_texte_trou_drag, chiffre_manquant_texte_trou_saisie
 *   regroupement_qcm_unique, regroupement_texte, regroupement_qcm_multiple
 *   ordre_grandeur_qcm_unique, ordre_grandeur_texte, ordre_grandeur_relier
 *   terme_manquant_qcm_unique, terme_manquant_texte
 *   complement_un_qcm_unique, complement_un_texte
 *   suite_logique_qcm_unique, suite_logique_texte, suite_logique_texte_trou_saisie
 *
 * Labels EXERCICES_REFERENCE.md :
 *   - Effectuer une addition ou soustraction posée
 *   - Poser en colonnes puis effectuer (traité en interne par calcul_direct)
 *   - Compléter les chiffres manquants d'une opération
 *   - Effectuer une somme ou différence en ligne
 *   - Calculer avec des regroupements astucieux
 *   - Estimer l'ordre de grandeur d'un calcul
 *   - Retrouver un terme manquant d'une opération
 *   - Trouver le complément à 1
 *   - Compléter une suite logique de décimaux
 *
 * Variantes estimées : 1000+ combinaisons
 */

import {
    shuffle, randInt, randomChoice, randomChoiceMultiple,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════════════
// CONSTANTES ET LABELS
// ═══════════════════════════════════════════════════════════════════

const ATTENDU_ID = 609;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALCUL    = 'Effectuer une addition ou soustraction posée';
const L_LIGNE     = 'Effectuer une somme ou différence en ligne';
const L_CHIFFRE   = "Compléter les chiffres manquants d'une opération";
const L_REGROUP   = 'Calculer avec des regroupements astucieux';
const L_ORDRE     = "Estimer l'ordre de grandeur d'un calcul";
const L_TERME     = "Retrouver un terme manquant d'une opération";
const L_COMPLEMENT = 'Trouver le complément à 1';
const L_SUITE     = 'Compléter une suite logique de décimaux';

// ═══════════════════════════════════════════════════════════════════
// HELPERS D'ARITHMÉTIQUE DÉCIMALE
// Toujours manipuler en entiers scaled pour éviter les erreurs flottantes
// Précision : jusqu'à 3 décimales (millièmes)
// ═══════════════════════════════════════════════════════════════════

const SCALE = 1000; // 3 décimales max

const toScaled = (s) => {
    const n = parseFloat(String(s).replace(',', '.'));
    return Math.round(n * SCALE);
};

const fromScaled = (n) => {
    const neg = n < 0 ? '-' : '';
    const abs = Math.abs(n);
    const ent = Math.floor(abs / SCALE);
    const dec = abs % SCALE;
    if (dec === 0) return `${neg}${ent}`;
    const decStr = dec.toString().padStart(3, '0').replace(/0+$/, '');
    return `${neg}${ent},${decStr}`;
};

const addDec = (a, b) => fromScaled(toScaled(a) + toScaled(b));
const subDec = (a, b) => fromScaled(toScaled(a) - toScaled(b));

// Nombre de décimales d'une écriture à virgule
const nbDec = (s) => {
    const str = String(s).replace(',', '.');
    const i = str.indexOf('.');
    return i < 0 ? 0 : str.length - i - 1;
};

// Formate un entier scaled avec un nombre de décimales fixe (ajoute des zéros)
const formatDecFixed = (scaled, decimales) => {
    const neg = scaled < 0 ? '-' : '';
    const abs = Math.abs(scaled);
    const factor = Math.pow(10, 3 - decimales);
    const rounded = Math.round(abs / factor);
    const base = Math.pow(10, decimales);
    const ent = Math.floor(rounded / base);
    const dec = rounded % base;
    if (decimales === 0) return `${neg}${ent}`;
    return `${neg}${ent},${dec.toString().padStart(decimales, '0')}`;
};

// Génère un décimal aléatoire avec `d` décimales, partie entière entre entMin et entMax
const randDecimal = (d, entMin, entMax) => {
    const ent = randInt(entMin, entMax);
    if (d === 0) return String(ent);
    const dec = randInt(0, Math.pow(10, d) - 1);
    return `${ent},${dec.toString().padStart(d, '0')}`;
};

// Arrondit un décimal (string) à un rang donné
// rang = 0 → unité, 1 → dizaine, 2 → centaine, -1 → dixième, etc.
const arrondirRang = (s, rang) => {
    const scaled = toScaled(s);
    if (rang <= 0) {
        const d = -rang; // nb décimales à garder
        return formatDecFixed(scaled, d);
    }
    const factor = SCALE * Math.pow(10, rang);
    const rounded = Math.round(scaled / factor) * factor;
    return fromScaled(rounded);
};

// ═══════════════════════════════════════════════════════════════════
// SVG INLINE — opération posée en colonnes
// ═══════════════════════════════════════════════════════════════════

/**
 * Construit une string KaTeX représentant une opération posée a OP b [= result]
 * via \begin{array}{r} ... \end{array}. Chiffres manquants représentés par \square.
 *
 * @param {string} a - Opérande 1 (ex: "34,9")
 * @param {string} b - Opérande 2
 * @param {string} op - '+' ou '−'
 * @param {string|null} result - Résultat (optionnel)
 * @param {Array<number[]>|null} cases - Positions à masquer par ligne [caseA, caseB, caseR]
 * @returns {string} Formule KaTeX à injecter dans un élément expression
 *
 * Pattern de rendu : on l'encapsule dans elements_additionnels avec
 * `{ type: 'expression', config: { contenu: latex } }` et on le référence
 * dans le texte via `{{element:0}}`. Le rendu passe par KaTeX donc les
 * couleurs héritent automatiquement du thème (visible dark + light).
 */
const buildPoseeKatex = (a, b, op, result = null, cases = null) => {
    // Aligner sur le max de décimales (pour virgules alignées en colonne)
    const nd = Math.max(nbDec(a), nbDec(b), result ? nbDec(result) : 0);
    const fmt = (s) => {
        if (s == null) return null;
        return formatDecFixed(toScaled(s), nd);
    };
    const aStr = fmt(a);
    const bStr = fmt(b);
    const rStr = result != null ? fmt(result) : null;
    const sign = op === '+' ? '+' : '-';

    // Remplace les chiffres aux positions spécifiées par \square, et toutes les
    // virgules par {,} pour supprimer l'espace de ponctuation KaTeX.
    const toLatex = (str, positions) => {
        const chars = str.split('');
        if (positions && positions.length > 0) {
            positions.forEach((pos) => {
                if (pos >= 0 && pos < chars.length) chars[pos] = '\\square';
            });
        }
        // Espace fine entre les chiffres (revue 2026-08-28, demande prof) :
        // la posée était trop serrée. Espacement identique sur chaque ligne,
        // donc les colonnes restent alignées (array right-aligned).
        return chars.map((c) => (c === ',' ? '{,}' : c)).join('\\,');
    };

    const aTex = toLatex(aStr, cases && cases[0]);
    const bTex = toLatex(bStr, cases && cases[1]);
    const rTex = rStr !== null ? toLatex(rStr, cases && cases[2]) : null;

    let latex = `\\begin{array}{r}\\phantom{${sign}\\,}${aTex} \\\\ ${sign}\\,${bTex} \\\\ \\hline`;
    if (rTex !== null) latex += ` ${rTex}`;
    latex += ` \\end{array}`;
    return latex;
};

// ═══════════════════════════════════════════════════════════════════
// T1 — CALCUL DIRECT (add/sous)
// ═══════════════════════════════════════════════════════════════════

// Tire une paire (a, b) avec variantes didactiques
const pickCalculDirectPair = (op) => {
    const variante = randInt(0, 4);
    let a, b;
    if (variante === 0) {
        // Même nombre de décimales (1 ou 2)
        const d = randInt(1, 2);
        a = randDecimal(d, 2, 50);
        b = randDecimal(d, 1, 30);
    } else if (variante === 1) {
        // Décimales différentes
        a = randDecimal(randInt(1, 2), 2, 50);
        b = randDecimal(randInt(1, 3), 1, 30);
    } else if (variante === 2) {
        // a entier, b décimal (cas 5 − 2,3)
        a = String(randInt(5, 80));
        b = randDecimal(randInt(1, 2), 1, Math.min(parseInt(a) - 1, 20));
    } else if (variante === 3) {
        // a a plus de partie entière, b petit
        a = randDecimal(1, 10, 99);
        b = randDecimal(2, 1, 9);
    } else {
        // Trois décimales (cas 0,527 + 1,206)
        a = randDecimal(3, 0, 5);
        b = randDecimal(3, 0, 5);
    }
    // Pour la soustraction, garantir a > b
    if (op === '−' && toScaled(a) <= toScaled(b)) {
        [a, b] = [b, a];
        // Marge de sécurité : si encore égaux, forcer
        if (toScaled(a) === toScaled(b)) a = addDec(a, '1');
    }
    return [a, b];
};

// Génère des distracteurs plausibles pour T1
const calculDirectDistractors = (correct, a, b, op) => {
    const cS = toScaled(correct);
    const aS = toScaled(a);
    const bS = toScaled(b);
    const cands = new Set();

    // D3 : oubli de retenue (±10 millièmes selon décimales)
    const stepRetenue = Math.pow(10, 3 - Math.max(nbDec(a), nbDec(b)));
    cands.add(fromScaled(cS - stepRetenue));
    cands.add(fromScaled(cS + stepRetenue));

    // D2 : opération inversée
    if (op === '+') {
        const inv = Math.abs(aS - bS);
        cands.add(fromScaled(inv));
    } else {
        cands.add(fromScaled(aS + bS));
    }

    // D1 : résultat tronqué aux décimales du plus court terme (simule un mauvais alignement)
    const minDec = Math.min(nbDec(a), nbDec(b));
    if (minDec < Math.max(nbDec(a), nbDec(b))) {
        const tronque = formatDecFixed(cS, minDec);
        cands.add(tronque);
    }

    // D5 : virgule décalée d'un rang (×10)
    cands.add(fromScaled(cS + stepRetenue * 10));

    // D6 : soustraction "colonne inversée" → approximation ±stepRetenue * 2
    cands.add(fromScaled(cS + stepRetenue * 2));

    // D7 (revue 2026-08-20) : oubli de la retenue qui TRAVERSE la virgule.
    // 21,7 + 1,8 : l'élève écrit 15 dixièmes → « 5 » et oublie la retenue
    // d'unité (22,5 au lieu de 23,5). Soustraction : emprunt oublié (+1).
    // C'est l'erreur qui change la partie entière du résultat.
    if (op === '+' && (aS % SCALE) + (bS % SCALE) >= SCALE) {
        if (cS - SCALE > 0) cands.add(fromScaled(cS - SCALE));
    }
    if (op === '−' && (aS % SCALE) < (bS % SCALE)) {
        cands.add(fromScaled(cS + SCALE));
    }

    // Nettoyage PAR VALEUR (revue 2026-08-15) : « 73,0 » et « 73 » sont le
    // même nombre — un distracteur de même valeur que la bonne rendait la
    // bonne réponse comptée fausse (8 tirages sur 5000).
    const vus = new Set([cS]);
    const propres = [];
    cands.forEach((c) => {
        if (c === '' || c === '0') return;
        const v = toScaled(c);
        if (vus.has(v)) return;
        vus.add(v);
        propres.push(c);
    });

    // Garantie de diversité (revue 2026-08-20) : au moins un distracteur avec
    // une partie entière différente de la bonne réponse, sinon les 4 choix
    // affichent le même entier et l'élève ne calcule plus que les décimales.
    const entierDe = (s) => String(s).split(',')[0];
    const eCorrect = entierDe(correct);
    const differents = propres.filter((c) => entierDe(c) !== eCorrect);
    if (differents.length === 0) return shuffle(propres).slice(0, 3);
    const prioritaire = randomChoice(differents);
    const reste = shuffle(propres.filter((c) => c !== prioritaire));
    return shuffle([prioritaire, ...reste.slice(0, 2)]);
};

const genCalculDirectQcm = () => {
    const op = Math.random() < 0.5 ? '+' : '−';
    const [a, b] = pickCalculDirectPair(op);
    const correct = op === '+' ? addDec(a, b) : subDec(a, b);
    const distractors = calculDirectDistractors(correct, a, b, op);
    while (distractors.length < 3) {
        // Fallback : ajouter un distracteur numérique aléatoire proche
        const alt = fromScaled(toScaled(correct) + randInt(1, 20));
        if (alt !== correct && !distractors.includes(alt)) distractors.push(alt);
    }
    const choix = [correct, ...distractors.slice(0, 3)];
    const posee = buildPoseeKatex(a, b, op);
    const texte = `<p>Quel est le résultat de cette ${op === '+' ? 'addition' : 'soustraction'} ?</p>{{element:0}}`;
    const elAdd = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: posee } }],
    };
    const p = makeQcm(choix, correct, texte, { ...opts(L_CALCUL), elements_additionnels: elAdd });
    p._meta.generatorKey = 'calcul_direct';
    return p;
};

const genCalculDirectTexte = () => {
    const op = Math.random() < 0.5 ? '+' : '−';
    const [a, b] = pickCalculDirectPair(op);
    const correct = op === '+' ? addDec(a, b) : subDec(a, b);
    const texte = `<p>Calcule : <strong>${a} ${op} ${b}</strong></p>`;
    const p = makeSaisie(correct, texte, opts(L_LIGNE));
    p._meta.generatorKey = 'calcul_direct';
    return p;
};

// Revue 2026-08-20 : construction INVERSÉE. Avec 4 opérations indépendantes,
// l'appariement se faisait par simple estimation (90,2 % de réussite mesurée
// sans poser un calcul). On tire désormais 4 résultats à exactement
// 2 décimales dans une fenêtre souple (partie entière Z ou Z+1), puis on
// remonte aux opérandes ; mix garanti 2 additions + 2 soustractions mélangées.
const genCalculDirectRelier = () => {
    const Z = randInt(2, 59);
    const ops = shuffle(['+', '+', '−', '−']);
    const pairs = [];
    const usedTargets = new Set();
    const usedSources = new Set();
    let safety = 0;
    while (pairs.length < 4 && safety < 80) {
        safety++;
        const op = ops[pairs.length];
        // Résultat : Z ou Z+1 en partie entière, centième NON NUL pour que
        // l'affichage garde exactement 2 décimales après strip des zéros.
        const c = randInt(1, 99);
        if (c % 10 === 0) continue;
        const rS = (Z + randInt(0, 1)) * SCALE + c * 10;
        if (usedTargets.has(rS)) continue;
        // Opérande b à 1 ou 2 décimales, puis a reconstruit depuis le résultat
        const db = randInt(1, 2);
        const step = SCALE / Math.pow(10, db);
        const bMaxS = op === '+' ? rS - SCALE : 20 * SCALE; // addition : garder a ≥ 1
        const bS = randInt(1, Math.floor(bMaxS / step)) * step;
        if (bS < 100) continue; // b ≥ 0,1
        const aS = op === '+' ? rS - bS : rS + bS;
        if (aS <= 0) continue;
        const src = `${fromScaled(aS)} ${op} ${fromScaled(bS)}`;
        if (usedSources.has(src)) continue;
        usedTargets.add(rS);
        usedSources.add(src);
        pairs.push({ source: src, target: fromScaled(rS) });
    }
    // Repli (quasi impossible) : compléter avec des opérations libres
    let safety2 = 0;
    while (pairs.length < 4 && safety2 < 50) {
        safety2++;
        const op = Math.random() < 0.5 ? '+' : '−';
        const [a, b] = pickCalculDirectPair(op);
        const r = op === '+' ? addDec(a, b) : subDec(a, b);
        const src = `${a} ${op} ${b}`;
        if (usedSources.has(src) || pairs.some((q) => q.target === r)) continue;
        usedSources.add(src);
        pairs.push({ source: src, target: r });
    }
    const texte = '<p>Relie chaque calcul à son résultat.</p>';
    const p = makeRelier(pairs, texte, opts(L_CALCUL));
    p._meta.generatorKey = 'calcul_direct';
    return p;
};

const genCalculDirectTableauVF = () => {
    const lignes = [];
    const bonnesReponses = [];
    const seen = new Set();
    let safety = 0;
    while (lignes.length < 4 && safety < 50) {
        safety++;
        const op = Math.random() < 0.5 ? '+' : '−';
        const [a, b] = pickCalculDirectPair(op);
        const correct = op === '+' ? addDec(a, b) : subDec(a, b);
        const vraie = Math.random() < 0.5;
        let affiche;
        if (vraie) {
            affiche = correct;
        } else {
            // Distracteur réaliste
            const distractors = calculDirectDistractors(correct, a, b, op);
            if (!distractors.length) continue;
            affiche = distractors[0];
        }
        const ligne = `${a} ${op} ${b} = ${affiche}`;
        if (seen.has(ligne)) continue;
        seen.add(ligne);
        lignes.push(ligne);
        bonnesReponses.push(vraie ? 0 : 1);
    }
    // Équilibrer : au moins 1 Vrai et 1 Faux
    if (!bonnesReponses.includes(0) || !bonnesReponses.includes(1)) {
        return genCalculDirectTableauVF(); // retry
    }
    const texte = '<p>Ces égalités sont-elles <strong>vraies</strong> ou <strong>fausses</strong> ?</p>';
    const p = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_CALCUL));
    p._meta.generatorKey = 'calcul_direct';
    return p;
};

// ═══════════════════════════════════════════════════════════════════
// T2 — CHIFFRE MANQUANT
// ═══════════════════════════════════════════════════════════════════

// Génère une opération a OP b = result avec 1 chiffre masqué.
// Retourne { a, b, op, result, hiddenLine (0|1|2), hiddenPos (index dans la ligne formatée), digit (le chiffre correct) }
const buildChiffreManquantOperation = () => {
    const op = Math.random() < 0.5 ? '+' : '−';
    const d = randInt(1, 2);
    let a = randDecimal(d, 10, 99);
    let b = randDecimal(d, 1, Math.min(99, Math.floor(parseFloat(a.replace(',', '.'))) - 1));
    if (op === '−' && toScaled(a) <= toScaled(b)) {
        [a, b] = [b, a];
    }
    const result = op === '+' ? addDec(a, b) : subDec(a, b);

    // Formater avec même nb décimales
    const nd = Math.max(nbDec(a), nbDec(b), nbDec(result));
    const aStr = formatDecFixed(toScaled(a), nd);
    const bStr = formatDecFixed(toScaled(b), nd);
    const rStr = formatDecFixed(toScaled(result), nd);

    // Choisir la ligne et la position du chiffre à masquer
    const lignes = [aStr, bStr, rStr];
    const hiddenLine = randInt(0, 2);
    const chosen = lignes[hiddenLine];
    // Index des chiffres (éviter la virgule)
    const digitIndices = chosen.split('').map((c, i) => /\d/.test(c) ? i : -1).filter(i => i >= 0);
    const hiddenPos = randomChoice(digitIndices);
    const digit = chosen[hiddenPos];

    return { a, b, op, result, aStr, bStr, rStr, hiddenLine, hiddenPos, digit };
};

const genChiffreManquantQcm = () => {
    const o = buildChiffreManquantOperation();
    // Construire la posée KaTeX avec un \square à la position manquante
    const cases = [[], [], []];
    cases[o.hiddenLine] = [o.hiddenPos];
    const posee = buildPoseeKatex(o.a, o.b, o.op, o.result, cases);

    // Distracteurs : chiffres voisins (±1, ±2, ±3) distincts
    const digit = parseInt(o.digit, 10);
    const candidats = [-1, 1, -2, 2, 3, -3]
        .map(delta => ((digit + delta) % 10 + 10) % 10)
        .filter(n => n !== digit);
    const distractors = [...new Set(candidats)].slice(0, 3).map(String);

    const choix = [o.digit, ...distractors];
    const texte = `<p>Quel chiffre manque dans la case ?</p>{{element:0}}`;
    const elAdd = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: posee } }],
    };
    const p = makeQcm(choix, o.digit, texte, { ...opts(L_CHIFFRE), elements_additionnels: elAdd });
    p._meta.generatorKey = 'chiffre_manquant';
    return p;
};

const genChiffreManquantTexte = () => {
    const o = buildChiffreManquantOperation();
    // Même posée que le QCM, avec \square à la position manquante
    const cases = [[], [], []];
    cases[o.hiddenLine] = [o.hiddenPos];
    const posee = buildPoseeKatex(o.a, o.b, o.op, o.result, cases);

    const texte = `<p>Quel chiffre manque dans la case ?</p>{{element:0}}`;
    const elAdd = {
        version: 1,
        elements: [{ type: 'expression', config: { contenu: posee } }],
    };
    const p = makeSaisie(o.digit, texte, { ...opts(L_CHIFFRE), strict: true, elements_additionnels: elAdd });
    p._meta.generatorKey = 'chiffre_manquant';
    return p;
};

const genChiffreManquantDrag = () => {
    const o = buildChiffreManquantOperation();
    // Phrase avec {{digit}} à la bonne position
    const makeLigne = (str, i) => {
        if (i !== o.hiddenLine) return str;
        return str.slice(0, o.hiddenPos) + `{{${o.digit}}}` + str.slice(o.hiddenPos + 1);
    };
    const aDisp = makeLigne(o.aStr, 0);
    const bDisp = makeLigne(o.bStr, 1);
    const rDisp = makeLigne(o.rStr, 2);
    // Espaces insécables autour de l'opérateur et du « = » (revue 2026-08-28) :
    // les espaces simples s'écrasent au rendu HTML, la ligne était trop serrée.
    const phrase = `${aDisp}  ${o.op}  ${bDisp}  =  ${rDisp}`;

    const digit = parseInt(o.digit, 10);
    const candidats = [-1, 1, -2, 2, -3, 3, 4, 5]
        .map(delta => ((digit + delta) % 10 + 10) % 10)
        .filter(n => n !== digit);
    const distractors = [...new Set(candidats)].slice(0, 4).map(String);

    const texte = '<p>Glisse le chiffre qui convient dans la case.</p>';
    const p = makeTexteTrou(phrase, texte, {
        ...opts(L_CHIFFRE),
        mode: 'drag',
        distracteurs: distractors,
        strict: true,
    });
    p._meta.generatorKey = 'chiffre_manquant';
    return p;
};

// GARDE-FOU D'UNICITÉ : brute-force des 100 combinaisons des DEUX chiffres cachés.
// Renvoie true si l'opération n'admet QU'UNE solution (a op b = result). Sans ce
// contrôle, deux trous partageant une colonne sous-contrainte (ex. dizaine du 1er
// terme + dizaine du résultat) rendent la réponse non unique : (7,0),(8,1),(9,2)…
const estUniqueSolutionDeuxTrous = (o) => {
    const toNum = (s) => Number(s.replace(',', '.'));
    const soustraction = !(o.op === '+');
    let count = 0;
    for (let d1 = 0; d1 <= 9; d1++) {
        for (let d2 = 0; d2 <= 9; d2++) {
            const arr = [o.aStr, o.bStr, o.rStr];
            arr[o.hiddenLine] = arr[o.hiddenLine].slice(0, o.hiddenPos) + d1 + arr[o.hiddenLine].slice(o.hiddenPos + 1);
            arr[o.hiddenLine2] = arr[o.hiddenLine2].slice(0, o.hiddenPos2) + d2 + arr[o.hiddenLine2].slice(o.hiddenPos2 + 1);
            const av = toNum(arr[0]), bv = toNum(arr[1]);
            const res = soustraction ? av - bv : av + bv;
            if (Math.abs(res - toNum(arr[2])) < 1e-9) {
                count++;
                if (count > 1) return false;
            }
        }
    }
    return count === 1;
};

// Construit la phrase à trous à partir de o (1 ou 2 trous selon hiddenLine2/hiddenPos2).
const buildPhraseChiffreManquant = (o) => {
    const makeLigne = (str, lineIdx) => {
        const positions = [];
        if (lineIdx === o.hiddenLine) positions.push([o.hiddenPos, o.digit]);
        if (lineIdx === o.hiddenLine2 && !(o.hiddenLine2 === o.hiddenLine && o.hiddenPos2 === o.hiddenPos)) {
            positions.push([o.hiddenPos2, o.digit2]);
        }
        if (!positions.length) return str;
        positions.sort((x, y) => y[0] - x[0]); // de droite à gauche pour ne pas décaler les indices
        let out = str;
        for (const [p, dg] of positions) out = out.slice(0, p) + `{{${dg}}}` + out.slice(p + 1);
        return out;
    };
    // Espaces insécables autour de l'opérateur et du « = » (revue 2026-08-28).
    return `${makeLigne(o.aStr, 0)}  ${o.op}  ${makeLigne(o.bStr, 1)}  =  ${makeLigne(o.rStr, 2)}`;
};

const genChiffreManquantSaisieTrou = () => {
    const makePayload = (o) => {
        const p = makeTexteTrou(buildPhraseChiffreManquant(o), '<p>Tape les chiffres qui complètent cette opération.</p>', {
            ...opts(L_CHIFFRE),
            mode: 'saisie',
            distracteurs: [],
            strict: true,
        });
        p._meta.generatorKey = 'chiffre_manquant';
        return p;
    };

    // Version 2 chiffres masqués — on re-tire tant que l'unicité n'est pas garantie.
    for (let attempt = 0; attempt < 60; attempt++) {
        const o = buildChiffreManquantOperation();
        // Choisir une deuxième position distincte
        o.hiddenLine2 = randInt(0, 2);
        const ligne2 = [o.aStr, o.bStr, o.rStr][o.hiddenLine2];
        const digitIdx2 = ligne2.split('').map((c, i) => /\d/.test(c) ? i : -1).filter(i => i >= 0);
        const candidates2 = digitIdx2.filter(i => !(o.hiddenLine === o.hiddenLine2 && i === o.hiddenPos));
        if (!candidates2.length) {
            o.hiddenLine2 = (o.hiddenLine + 1) % 3;
            const di2b = [o.aStr, o.bStr, o.rStr][o.hiddenLine2].split('').map((c, i) => /\d/.test(c) ? i : -1).filter(i => i >= 0);
            o.hiddenPos2 = randomChoice(di2b);
        } else {
            o.hiddenPos2 = randomChoice(candidates2);
        }
        o.digit2 = [o.aStr, o.bStr, o.rStr][o.hiddenLine2][o.hiddenPos2];

        if (estUniqueSolutionDeuxTrous(o)) return makePayload(o);
    }

    // Fallback : UN SEUL chiffre masqué → unicité garantie (comme buildChiffreManquantOperation).
    const o = buildChiffreManquantOperation();
    o.hiddenLine2 = o.hiddenLine;
    o.hiddenPos2 = o.hiddenPos;
    o.digit2 = o.digit;
    return makePayload(o);
};

// ═══════════════════════════════════════════════════════════════════
// T3 — REGROUPEMENT ASTUCIEUX
// ═══════════════════════════════════════════════════════════════════

// ─── Cas générés PAR CONSTRUCTION (enrichissement 2026-07-04) ───────────────
// Remplace le pool de 23 tuples figés. Invariants du pool conservés :
//  - chaque paire « astucieuse » = deux décimaux dont les parties
//    fractionnaires sont COMPLÉMENTAIRES (somme de la paire = entier) ;
//  - cas à 4 termes : 2 paires astucieuses arrangées NON-ADJACENTES
//    (termes = [a1, a2, b1, b2] avec paires (a1,b1) et (a2,b2)) ; les paires
//    adjacentes (a1+a2, b1+b2) ne tombent JAMAIS rond — garanti par
//    construction (fractions inter-paires non complémentaires), donc les
//    mauvais choix du QCM multi restent faux ;
//  - cas à 3 termes : 1 paire astucieuse + un terme isolé.

/** Paire de décimaux strictement positifs dont la somme est entière. */
const genPaireRonde609 = (dec) => {
    const base = Math.pow(10, dec);
    let f = randInt(1, base - 1);
    while (dec >= 2 && f % 10 === 0) f = randInt(1, base - 1); // pas de zéro final affiché
    const intA = randInt(0, 12);
    const intB = randInt(0, 12);
    const aff = (i, frac) => `${i},${String(frac).padStart(dec, '0')}`;
    return { a: aff(intA, f), b: aff(intB, base - f), f, dec, somme: intA + intB + 1 };
};

/** Cas à 4 termes / 2 paires astucieuses non-adjacentes. */
const genRegroupementCase4 = () => {
    for (let i = 0; i < 60; i++) {
        const d1 = randomChoice([1, 2, 2]);
        const d2 = randomChoice([1, 2, 2, 3]);
        const p1 = genPaireRonde609(d1);
        const p2 = genPaireRonde609(d2);
        // Interdit : fractions complémentaires ENTRE paires (les regroupements
        // adjacents a1+a2 / b1+b2 deviendraient ronds eux aussi)
        const b1 = Math.pow(10, d1);
        const b2 = Math.pow(10, d2);
        if (p1.f * b2 + p2.f * b1 === b1 * b2) continue;
        const termes = [p1.a, p2.a, p1.b, p2.b];
        if (new Set(termes).size < 4) continue;
        return {
            termes,
            somme: String(p1.somme + p2.somme),
            paires: [[p1.a, p1.b], [p2.a, p2.b]],
        };
    }
    // Repli déterministe (mêmes invariants, vérifié à la main)
    return { termes: ['1,25', '3,6', '6,75', '2,4'], somme: '14', paires: [['1,25', '6,75'], ['3,6', '2,4']] };
};

/** Cas à 3 termes : 1 paire astucieuse + un terme isolé (entier ou décimal). */
const genRegroupementCase3 = () => {
    for (let i = 0; i < 30; i++) {
        const p = genPaireRonde609(randomChoice([1, 2]));
        const isole = Math.random() < 0.5
            ? String(randInt(2, 15))
            : `${randInt(1, 15)},${randInt(1, 9)}`;
        const termes = shuffle([p.a, p.b, isole]);
        if (new Set(termes).size < 3) continue;
        return { termes, somme: addDec(String(p.somme), isole), paires: [[p.a, p.b]] };
    }
    return { termes: ['6,5', '12,6', '1,5'], somme: '20,6', paires: [['6,5', '1,5']] };
};

const genRegroupementCase = () => (Math.random() < 0.4 ? genRegroupementCase3() : genRegroupementCase4());



const genRegroupementQcm = () => {
    const ex = genRegroupementCase();
    const affiche = shuffle(ex.termes).join(' + ');
    const correct = ex.somme;
    // Distracteurs de MÊME NATURE que la bonne (revue 2026-08-15) : la bonne
    // était le seul entier des 4 choix dans 73 % des tirages — « je coche le
    // compte rond » gagnait sans calcul. Et un panachage 3 entiers + 1 décimal
    // aurait rendu le décimal éliminable : les 4 choix partagent la nature.
    const cS = toScaled(correct);
    const distractors = [];
    if (cS % SCALE === 0) {
        // Somme entière → distracteurs entiers
        distractors.push(fromScaled(cS - SCALE));       // retenue oubliée
        distractors.push(fromScaled(cS + SCALE));       // retenue en trop
        // Oubli d'une paire astucieuse ENTIÈRE (reste entier)
        const paire = randomChoice(ex.paires);
        const somPaire = toScaled(paire[0]) + toScaled(paire[1]);
        distractors.push(fromScaled(cS - somPaire));
    } else {
        // Somme décimale → distracteurs décimaux au même nombre de décimales
        const dec = nbDec(correct);
        const pas = Math.pow(10, 3 - dec); // dernier rang affiché
        distractors.push(formatDecFixed(cS - pas, dec));
        distractors.push(formatDecFixed(cS + pas, dec));
        distractors.push(formatDecFixed(cS + 2 * pas, dec));
    }
    // En branche décimale, une valeur ENTIÈRE (ex. 13,9 + 0,1 → « 14,0 »)
    // trahirait la nature : rejetée et remplacée.
    const sommeEntiere = cS % SCALE === 0;
    const natureOk = (v) => (sommeEntiere ? true : v % SCALE !== 0);
    const vus = new Set([cS]);
    const unique = [];
    distractors.forEach((d) => {
        const v = toScaled(d);
        if (vus.has(v) || v <= 0 || !natureOk(v)) return;
        vus.add(v);
        unique.push(d);
    });
    while (unique.length < 3) {
        const alt = sommeEntiere
            ? fromScaled(cS + randInt(2, 6) * SCALE)
            : formatDecFixed(cS + randInt(3, 9) * Math.pow(10, 3 - nbDec(correct)), nbDec(correct));
        const v = toScaled(alt);
        if (!vus.has(v) && v > 0 && natureOk(v)) { vus.add(v); unique.push(alt); }
    }
    const choix = [correct, ...unique];
    const texte = `<p>Calcule en effectuant un regroupement astucieux (regroupe les nombres qui forment ensemble un compte rond) :</p><p><strong>${affiche}</strong></p>`;
    const p = makeQcm(choix, correct, texte, opts(L_REGROUP));
    p._meta.generatorKey = 'regroupement';
    return p;
};

const genRegroupementTexte = () => {
    const ex = genRegroupementCase();
    const affiche = shuffle(ex.termes).join(' + ');
    const texte = `<p>Calcule en regroupant astucieusement : <strong>${affiche}</strong></p>`;
    const p = makeSaisie(ex.somme, texte, opts(L_REGROUP));
    p._meta.generatorKey = 'regroupement';
    return p;
};

const genRegroupementQcmMulti = () => {
    // Toujours un cas à 4 termes / 2 paires astucieuses (non-adjacentes)
    const ex = genRegroupementCase4();
    // Paires correctes
    const correctes = ex.paires.map(p => `${p[0]} + ${p[1]}`);
    // Paires non astucieuses (termes adjacents dans l'écriture)
    const nonAstu = [
        `${ex.termes[0]} + ${ex.termes[1]}`,
        `${ex.termes[2]} + ${ex.termes[3]}`,
    ].filter(s => !correctes.includes(s) && !correctes.includes(s.split(' + ').reverse().join(' + ')));
    const choix = shuffle([...correctes, ...nonAstu.slice(0, 2)]);
    // S'assurer qu'on a au moins 4 choix
    while (choix.length < 4) {
        const extra = `${ex.termes[randInt(0, 3)]} + ${ex.termes[randInt(0, 3)]}`;
        if (!choix.includes(extra)) choix.push(extra);
    }
    const texte = `<p>Pour calculer <strong>${ex.termes.join(' + ')}</strong> astucieusement, quelles paires dois-tu regrouper ?</p><p>Sélectionne les <strong>deux</strong> paires utiles.</p>`;
    const p = makeQcm(choix.slice(0, 4), correctes.filter(c => choix.includes(c)), texte, opts(L_REGROUP), true);
    // S'assurer que les deux correctes sont dans les 4 choix
    if (!correctes.every(c => choix.slice(0, 4).includes(c))) {
        // Reprendre avec correctes forcées
        const final = [...correctes, ...nonAstu.slice(0, 2)].slice(0, 4);
        const p2 = makeQcm(final, correctes, texte, opts(L_REGROUP), true);
        p2._meta.generatorKey = 'regroupement';
        return p2;
    }
    p._meta.generatorKey = 'regroupement';
    return p;
};

// ═══════════════════════════════════════════════════════════════════
// T4 — ORDRE DE GRANDEUR
// ═══════════════════════════════════════════════════════════════════

/**
 * Génère un cas d'ordre de grandeur avec 3 termes.
 *
 * Principe pédagogique : le rang d'arrondi s'adapte à l'ordre de grandeur
 * des nombres pour donner des valeurs "simples" à additionner mentalement :
 *  - Petits décimaux [10;100]   → arrondi à la dizaine   (20 + 20 + 10 = 50)
 *  - Moyens décimaux [100;1000] → arrondi à la centaine  (200 + 500 + 100 = 800)
 *  - Grands entiers [1000;9999] → arrondi au millier     (1000 + 6000 + 2000 = 9000)
 *
 * L'ordre de grandeur n'est pas un simple "arrondi à l'unité" — c'est un
 * nombre qui donne la MAGNITUDE rapidement. Le rang est choisi en fonction
 * de la taille des nombres pour préserver cette simplicité cognitive.
 */
const pickOrdreGrandeurCase = () => {
    const r = Math.random();
    let rang, terms;
    if (r < 0.5) {
        // Petits décimaux → arrondi à la dizaine
        rang = 1;
        terms = [
            randDecimal(2, 10, 99),
            randDecimal(2, 10, 99),
            randDecimal(2, 10, 99),
        ];
    } else if (r < 0.85) {
        // Décimaux moyens → arrondi à la centaine
        rang = 2;
        terms = [
            randDecimal(1, 100, 999),
            randDecimal(1, 100, 999),
            randDecimal(1, 100, 999),
        ];
    } else {
        // Grands entiers → arrondi au millier
        rang = 3;
        terms = [
            randDecimal(0, 1000, 9999),
            randDecimal(0, 1000, 9999),
            randDecimal(0, 1000, 9999),
        ];
    }

    // Ordre de grandeur = somme des arrondis au rang choisi
    let ordre = arrondirRang(terms[0], rang);
    for (let i = 1; i < terms.length; i++) {
        ordre = addDec(ordre, arrondirRang(terms[i], rang));
    }

    return { terms, ordre, rang };
};

/** Libellé du rang d'arrondi pour l'énoncé. */
const rangLabel = (rang) => {
    switch (rang) {
        case 1: return 'à la dizaine';
        case 2: return 'à la centaine';
        case 3: return 'au millier';
        default: return "à l'unité";
    }
};

const genOrdreGrandeurQcm = () => {
    const c = pickOrdreGrandeurCase();
    const label = rangLabel(c.rang);
    const expr = c.terms.join(' + ');

    // Somme exacte (distracteur "trop précis" — piège l'élève qui n'arrondit pas)
    let exact = c.terms[0];
    for (let i = 1; i < c.terms.length; i++) exact = addDec(exact, c.terms[i]);

    const distractors = new Set();
    distractors.add(exact);
    distractors.add(fromScaled(toScaled(c.ordre) + Math.pow(10, c.rang + 3))); // ordre +1 rang
    distractors.add(fromScaled(toScaled(c.ordre) - Math.pow(10, c.rang + 3))); // ordre -1 rang
    distractors.delete(c.ordre);
    distractors.delete('');
    const dist = [...distractors].slice(0, 3);
    while (dist.length < 3) {
        const alt = fromScaled(toScaled(c.ordre) + randInt(2, 9) * Math.pow(10, c.rang + 3));
        if (alt !== c.ordre && !dist.includes(alt)) dist.push(alt);
    }
    const choix = [c.ordre, ...dist];
    const texte = `<p>Donne un ordre de grandeur de <strong>${expr}</strong> en arrondissant chaque nombre ${label}.</p>`;
    const p = makeQcm(choix, c.ordre, texte, opts(L_ORDRE));
    p._meta.generatorKey = 'ordre_grandeur';
    return p;
};

const genOrdreGrandeurTexte = () => {
    const c = pickOrdreGrandeurCase();
    const label = rangLabel(c.rang);
    const expr = c.terms.join(' + ');
    const texte = `<p>Arrondis chaque nombre ${label}, puis donne l'ordre de grandeur de <strong>${expr}</strong>.</p>`;
    const p = makeSaisie(c.ordre, texte, opts(L_ORDRE));
    p._meta.generatorKey = 'ordre_grandeur';
    return p;
};

const genOrdreGrandeurRelier = () => {
    const pairs = [];
    const used = new Set();
    let safety = 0;
    while (pairs.length < 4 && safety < 30) {
        safety++;
        const c = pickOrdreGrandeurCase();
        const src = c.terms.join(' + ');
        if (used.has(src) || pairs.some(p => p.target === c.ordre)) continue;
        pairs.push({ source: src, target: c.ordre });
        used.add(src);
    }
    const texte = "<p>Relie chaque calcul à son ordre de grandeur (arrondi à la dizaine, à la centaine ou au millier selon la taille des nombres).</p>";
    const p = makeRelier(pairs, texte, opts(L_ORDRE));
    p._meta.generatorKey = 'ordre_grandeur';
    return p;
};

// ═══════════════════════════════════════════════════════════════════
// T5 — TERME MANQUANT
// ═══════════════════════════════════════════════════════════════════

// Opérations à trou non ambiguës :
//   'somme'       : a + ? = s          → inconnu = s − a
//   'diff_droite' : a − ? = d          → inconnu = a − d   (avec a > d)
//   'diff_gauche' : ? − b = d          → inconnu = b + d
// Les valeurs sont toutes normalisées via fromScaled(toScaled(...)) pour
// supprimer les zéros trailing inutiles (évite "21,00" vs "21" en saisie stricte).
const norm = (s) => fromScaled(toScaled(s));
// Revue 2026-08-23 (choix prof) : opérandes à 2 décimales STRICTES (dernier
// chiffre non nul, sinon le strip des zéros réafficherait 1 décimale).
// Les valeurs DÉRIVÉES (somme, différence) gardent leur écriture canonique.
const randDecimal2Strict = (entMin, entMax) => {
    const ent = randInt(entMin, entMax);
    let frac = randInt(1, 99);
    if (frac % 10 === 0) frac += 1;
    return `${ent},${String(frac).padStart(2, '0')}`;
};
const pickTermeManquantCase = () => {
    const forme = randomChoice(['somme', 'diff_droite', 'diff_gauche']);
    if (forme === 'somme') {
        const a = randDecimal2Strict(1, 40);
        const inconnu = randDecimal2Strict(1, 40);
        const s = addDec(a, inconnu);
        return { forme, a, inconnu, s };
    }
    if (forme === 'diff_gauche') {
        // ? − b = diff
        const b = randDecimal2Strict(1, 40);
        const diff = randDecimal2Strict(1, 40);
        const inconnu = addDec(b, diff);
        return { forme, b, diff, inconnu };
    }
    // diff_droite : a − ? = diff, a > diff
    const inconnu = randDecimal2Strict(1, 40);
    const diff = randDecimal2Strict(1, 40);
    const a = addDec(inconnu, diff);
    return { forme, a, diff, inconnu };
};

const enonceTerme = (c) => {
    if (c.forme === 'somme') {
        return `<p>Quel nombre complète cette égalité ?</p><p><strong>${c.a} + … = ${c.s}</strong></p>`;
    }
    if (c.forme === 'diff_gauche') {
        return `<p>Quel nombre complète cette égalité ?</p><p><strong>… − ${c.b} = ${c.diff}</strong></p>`;
    }
    return `<p>Quel nombre complète cette égalité ?</p><p><strong>${c.a} − … = ${c.diff}</strong></p>`;
};

const genTermeManquantQcm = () => {
    const c = pickTermeManquantCase();
    const correct = c.inconnu;
    const cS = toScaled(correct);
    const distractors = new Set();
    // D10 : inversion d'opération (l'élève ajoute au lieu de soustraire, ou vice versa)
    if (c.forme === 'somme') {
        distractors.add(fromScaled(toScaled(c.s) + toScaled(c.a))); // addition au lieu de soustr.
    } else if (c.forme === 'diff_gauche') {
        distractors.add(fromScaled(Math.abs(toScaled(c.b) - toScaled(c.diff)))); // soustr au lieu de +
    } else {
        distractors.add(fromScaled(toScaled(c.a) + toScaled(c.diff))); // add au lieu de soustr
    }
    distractors.add(fromScaled(cS + 10));
    distractors.add(fromScaled(cS - 10));
    distractors.delete(correct);
    distractors.delete('');
    const dist = [...distractors].slice(0, 3);
    while (dist.length < 3) {
        const alt = fromScaled(cS + randInt(20, 200));
        if (alt !== correct && !dist.includes(alt)) dist.push(alt);
    }
    const choix = [correct, ...dist];
    const p = makeQcm(choix, correct, enonceTerme(c), opts(L_TERME));
    p._meta.generatorKey = 'terme_manquant';
    return p;
};

const genTermeManquantTexte = () => {
    const c = pickTermeManquantCase();
    const p = makeSaisie(c.inconnu, enonceTerme(c), opts(L_TERME));
    p._meta.generatorKey = 'terme_manquant';
    return p;
};

// ═══════════════════════════════════════════════════════════════════
// T6 — COMPLÉMENT À 1
// ═══════════════════════════════════════════════════════════════════

const pickComplementCase = () => {
    // Revue 2026-08-23 (choix prof) : 2 décimales STRICTES — les compléments
    // à 3 décimales (1 − 0,234) sont trop durs pour l'intention du format.
    // Pool : 98 énoncés (0,01 → 0,99, sans 0,50), doublons non consécutifs
    // assumés comme sur les autres pools finis.
    const d = 2;
    let x;
    for (let safety = 0; safety < 20; safety++) {
        const val = randInt(1, Math.pow(10, d) - 1);
        if (val === Math.pow(10, d) / 2) continue; // exclure 0,5
        x = `0,${val.toString().padStart(d, '0')}`;
        if (!/^0,0+$/.test(x)) break;
    }
    // Fallback si les 20 tentatives tombent toutes à côté (quasi-impossible mais safe)
    if (!x) x = '0,37';
    const complement = subDec('1', x);
    return { x, complement };
};

// Distracteur D7 : complément à 9 sur chaque chiffre + dernier à 10.
// NB (revue 2026-08-19) : quand le dernier chiffre n'est pas 0, cette recette
// EST la bonne méthode — D7 recalcule alors la bonne réponse et se fait
// écarter par le dédoublonnage (92 % des tirages). Il ne survit que pour les
// x finissant par 0 affiché (ex. 0,40 → « 0,50 » ≠ 0,60), soit ~8 %.
const distracteurComplementD7 = (x) => {
    const decPart = x.split(',')[1] || '';
    const chars = decPart.split('');
    const last = chars.length - 1;
    const out = chars.map((c, i) => {
        const d = parseInt(c, 10);
        return i === last ? String((10 - d) % 10) : String(9 - d);
    }).join('');
    // Écriture canonique (revue 2026-08-23) : « 0,50 » → « 0,5 »
    return fromScaled(toScaled(`0,${out}`));
};

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureCompl609 = null;

const genComplementQcm = () => {
    let payload = construireComplementQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureCompl609; t++) {
        payload = construireComplementQcm();
    }
    derniereSignatureCompl609 = payload.texte;
    return payload;
};

const construireComplementQcm = () => {
    const c = pickComplementCase();
    const correct = c.complement;
    const distractors = new Set();
    distractors.add(distracteurComplementD7(c.x));
    // Autres : ±0,01 ; ±0,001
    const cS = toScaled(correct);
    distractors.add(fromScaled(cS + 10));
    distractors.add(fromScaled(cS - 10));
    distractors.delete(correct);
    distractors.delete('');
    const dist = [...distractors].slice(0, 3);
    while (dist.length < 3) {
        // Revue 2026-08-23 (choix prof) : distracteurs à 2 décimales MAX —
        // on ne décale que par pas de centièmes (multiples de 10 millièmes).
        const alt = fromScaled(cS + randInt(1, 9) * 10);
        if (alt !== correct && !dist.includes(alt)) dist.push(alt);
    }
    const choix = [correct, ...dist];
    const texte = `<p>Calcule : <strong>1 − ${c.x}</strong></p>`;
    const p = makeQcm(choix, correct, texte, opts(L_COMPLEMENT));
    p._meta.generatorKey = 'complement_un';
    return p;
};

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureComplTxt609 = null;

const genComplementTexte = () => {
    let payload = construireComplementTexte();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureComplTxt609; t++) {
        payload = construireComplementTexte();
    }
    derniereSignatureComplTxt609 = payload.texte;
    return payload;
};

const construireComplementTexte = () => {
    const c = pickComplementCase();
    const texte = `<p>Trouve le complément à 1 de <strong>${c.x}</strong>. (Autrement dit, calcule 1 − ${c.x}.)</p>`;
    const p = makeSaisie(c.complement, texte, opts(L_COMPLEMENT));
    p._meta.generatorKey = 'complement_un';
    return p;
};

// ═══════════════════════════════════════════════════════════════════
// T7 — SUITE LOGIQUE
// ═══════════════════════════════════════════════════════════════════

// Pool de (u0, pas en scaled)
const SUITE_POOL = [
    { u0: '0,676', pas: 1,   nbDec: 3 },  // pas 0,001
    { u0: '1,2',   pas: 200, nbDec: 1 },  // pas 0,2
    { u0: '3,25',  pas: 250, nbDec: 2 },  // pas 0,25 (changement de format)
    { u0: '0,1',   pas: 100, nbDec: 1 },  // pas 0,1
    { u0: '5,5',   pas: 500, nbDec: 1 },  // pas 0,5
    { u0: '2,15',  pas: 10,  nbDec: 2 },  // pas 0,01
    { u0: '10,3',  pas: 300, nbDec: 1 },  // pas 0,3
    { u0: '4,125', pas: 5,   nbDec: 3 },  // pas 0,005
];

/** Options de pas pédagogiquement simples pour la génération dynamique. */
const SUITE_PAS_OPTIONS = [
    { pas: 1, nbDec: 3 },     // 0,001
    { pas: 5, nbDec: 3 },     // 0,005
    { pas: 10, nbDec: 2 },    // 0,01
    { pas: 20, nbDec: 2 },    // 0,02
    { pas: 25, nbDec: 3 },    // 0,025
    { pas: 50, nbDec: 2 },    // 0,05
    { pas: 100, nbDec: 1 },   // 0,1
    { pas: 200, nbDec: 1 },   // 0,2
    { pas: 250, nbDec: 2 },   // 0,25
    { pas: 300, nbDec: 1 },   // 0,3
    { pas: 400, nbDec: 1 },   // 0,4
    { pas: 500, nbDec: 1 },   // 0,5
];

/** Génère dynamiquement un cfg de suite (u0 multiple de pas, plage [0,1 ; 20]). */
const buildSuiteDynamique = () => {
    const cfg = randomChoice(SUITE_PAS_OPTIONS);
    const kMin = Math.max(1, Math.ceil(100 / cfg.pas));    // u0 ≥ 0,1
    const kMax = Math.floor(20000 / cfg.pas);              // u0 ≤ 20
    const k = randInt(kMin, kMax);
    const u0S = k * cfg.pas;
    return { u0: formatDecFixed(u0S, cfg.nbDec), pas: cfg.pas, nbDec: cfg.nbDec };
};

const buildSuite = (count) => {
    // 30% pool curé (pédagogiquement tuned) / 70% dynamique (~500+ variantes)
    const cfg = Math.random() < 0.3 ? randomChoice(SUITE_POOL) : buildSuiteDynamique();
    const u0S = toScaled(cfg.u0);
    const termes = [];
    for (let i = 0; i < count; i++) {
        termes.push(formatDecFixed(u0S + i * cfg.pas, cfg.nbDec));
    }
    const next = formatDecFixed(u0S + count * cfg.pas, cfg.nbDec);
    return { termes, next, cfg };
};

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureSuiteQcm609 = null;

const genSuiteQcm = () => {
    let payload = construireSuiteQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSuiteQcm609; t++) {
        payload = construireSuiteQcm();
    }
    derniereSignatureSuiteQcm609 = payload.texte;
    return payload;
};

const construireSuiteQcm = () => {
    const s = buildSuite(4);
    const correct = s.next;
    const cS = toScaled(correct);
    // Distracteurs dédoublonnés PAR VALEUR (revue 2026-08-19) : l'« écriture
    // fantaisiste » ajoutait un zéro final à la bonne (« 5,70 » pour « 5,7 »),
    // soit la MÊME valeur — deux bonnes réponses dont une comptée fausse
    // dans 75 % des tirages. Supprimée, remplacée par le saut de deux pas.
    const candidats = [
        formatDecFixed(toScaled(s.termes[3]) + s.cfg.pas * 10, s.cfg.nbDec), // pas ×10
        formatDecFixed(toScaled(s.termes[3]) - s.cfg.pas, s.cfg.nbDec),      // pas inversé
        formatDecFixed(cS + s.cfg.pas, s.cfg.nbDec),                         // saut d'un terme
        formatDecFixed(cS + 2 * s.cfg.pas, s.cfg.nbDec),                     // saut de deux pas
    ];
    const vus = new Set([cS]);
    const dist = [];
    candidats.forEach((d) => {
        const v = toScaled(d);
        if (dist.length >= 3 || vus.has(v) || v <= 0) return;
        vus.add(v);
        dist.push(d);
    });
    while (dist.length < 3) {
        const alt = formatDecFixed(cS + randInt(3, 20) * s.cfg.pas, s.cfg.nbDec);
        const v = toScaled(alt);
        if (!vus.has(v) && v > 0) { vus.add(v); dist.push(alt); }
    }
    const choix = [correct, ...dist];
    const texte = `<p>Complète la suite logique. Quel est le nombre qui suit ?</p><p><strong>${s.termes.join(' ; ')} ; …</strong></p>`;
    const p = makeQcm(choix, correct, texte, opts(L_SUITE));
    p._meta.generatorKey = 'suite_logique';
    return p;
};

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureSuiteTxt609 = null;

const genSuiteTexte = () => {
    let payload = construireSuiteTexte();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSuiteTxt609; t++) {
        payload = construireSuiteTexte();
    }
    derniereSignatureSuiteTxt609 = payload.texte;
    return payload;
};

const construireSuiteTexte = () => {
    const s = buildSuite(4);
    const texte = `<p>Complète la suite logique :</p><p><strong>${s.termes.join(' ; ')} ; …</strong></p><p>Donne le nombre qui suit.</p>`;
    const p = makeSaisie(s.next, texte, opts(L_SUITE));
    p._meta.generatorKey = 'suite_logique';
    return p;
};

// Anti-répétition immédiate (revue 2026-08-19, recette standard).
let derniereSignatureSuiteTrou609 = null;

const genSuiteSaisieTrou = () => {
    let payload = construireSuiteSaisieTrou();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureSuiteTrou609; t++) {
        payload = construireSuiteSaisieTrou();
    }
    derniereSignatureSuiteTrou609 = payload.contenu.phrase;
    return payload;
};

const construireSuiteSaisieTrou = () => {
    const s = buildSuite(5);
    // Masquer 2 termes (positions 2 et 4)
    const phrase = [
        s.termes[0], s.termes[1], `{{${s.termes[2]}}}`, s.termes[3], `{{${s.termes[4]}}}`,
    ].join(' ; ');
    const texte = '<p>Complète la suite logique en tapant les nombres manquants.</p>';
    const p = makeTexteTrou(phrase, texte, {
        ...opts(L_SUITE),
        mode: 'saisie',
        distracteurs: [],
        strict: false,
    });
    p._meta.generatorKey = 'suite_logique';
    return p;
};

// ═══════════════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════════════

const GENERATORS = {
    calcul_direct_qcm_unique:      genCalculDirectQcm,
    calcul_direct_texte:           genCalculDirectTexte,
    calcul_direct_relier:          genCalculDirectRelier,
    calcul_direct_tableau_vf:      genCalculDirectTableauVF,

    chiffre_manquant_qcm_unique:        genChiffreManquantQcm,
    chiffre_manquant_texte:             genChiffreManquantTexte,
    chiffre_manquant_texte_trou_drag:   genChiffreManquantDrag,
    chiffre_manquant_texte_trou_saisie: genChiffreManquantSaisieTrou,

    regroupement_qcm_unique:   genRegroupementQcm,
    regroupement_texte:        genRegroupementTexte,
    regroupement_qcm_multiple: genRegroupementQcmMulti,

    ordre_grandeur_qcm_unique: genOrdreGrandeurQcm,
    ordre_grandeur_texte:      genOrdreGrandeurTexte,
    ordre_grandeur_relier:     genOrdreGrandeurRelier,

    terme_manquant_qcm_unique: genTermeManquantQcm,
    terme_manquant_texte:      genTermeManquantTexte,

    complement_un_qcm_unique: genComplementQcm,
    complement_un_texte:      genComplementTexte,

    suite_logique_qcm_unique:        genSuiteQcm,
    suite_logique_texte:             genSuiteTexte,
    suite_logique_texte_trou_saisie: genSuiteSaisieTrou,
};

// ═══════════════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════════════

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul_direct',    label: L_CALCUL,     formatsList: buildFormats('calcul_direct') },
    { id: 'chiffre_manquant', label: L_CHIFFRE,    formatsList: buildFormats('chiffre_manquant') },
    { id: 'regroupement',     label: L_REGROUP,    formatsList: buildFormats('regroupement') },
    { id: 'ordre_grandeur',   label: L_ORDRE,      formatsList: buildFormats('ordre_grandeur') },
    { id: 'terme_manquant',   label: L_TERME,      formatsList: buildFormats('terme_manquant') },
    { id: 'complement_un',    label: L_COMPLEMENT, formatsList: buildFormats('complement_un') },
    // Revue 2026-08-27 (choix prof) : la carte « suite logique » ENTIÈRE est
    // TRANSPLANTÉE au gen 638 (motifs évolutifs) ; masquée ici du catalogue,
    // générateurs conservés pour la banque.
    // { id: 'suite_logique',    label: L_SUITE,      formatsList: buildFormats('suite_logique') },
];

// ═══════════════════════════════════════════════════════════════════
// POINT D'ENTRÉE
// ═══════════════════════════════════════════════════════════════════

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            k => k.startsWith(typeExercice) || k.includes(typeExercice)
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};
